import React, { useMemo } from 'react';
import { formatCurrency } from 'utils';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ReadOnlyQuill } from './ReadOnlyQuill';
import { ProductConfig } from 'modules/desking/types';
import { FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/dist/generated';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

interface Props {
  product: ProposalProduct;
  productConfiguration: ProductConfig;
}

function getCoverageTermHumanReadableText(coverageTerm: string | undefined): string | null {
  if (!coverageTerm) {
    return null;
  }
  return parseInt(coverageTerm).toString() === coverageTerm ? `${coverageTerm} months` : coverageTerm;
}

export const ProductCatalogDetails = ({ product, productConfiguration }: Props) => {
  const classes = useStyles();

  const catalogDetails =
    productConfiguration?.aftermarketProduct?.aftermarketProductCatalogDetails ??
    product?.aftermarketProduct?.aftermarketProductCatalogDetails;

  const {
    isProductSummaryDataExist,
    isCoverageTermDataExist,
    isDeductibleDataExist,
    productSummary,
    coverageTerm,
    deductible,
  } = useMemo(() => {
    const data = {
      productSummary: catalogDetails?.productSummary,
      coverageTerm: getCoverageTermHumanReadableText(productConfiguration?.COVERAGE_TERM),
      deductible: productConfiguration?.deductible ? formatCurrency(productConfiguration?.deductible) : null,
    };

    return {
      ...data,
      isProductSummaryDataExist: data.productSummary,
      isCoverageTermDataExist: data.coverageTerm,
      isDeductibleDataExist: data.deductible,
    };
  }, [product, productConfiguration, catalogDetails]);

  return (
    <>
      <RenderWithCondition condition={!!product.aftermarketProduct?.productDescriptionInternal}>
        <Typography>{product.aftermarketProduct?.productDescriptionInternal}</Typography>
      </RenderWithCondition>

      {isCoverageTermDataExist && (
        <Box className={classes.container}>
          <Typography className={classes.bold}>Coverage Term:</Typography> {coverageTerm}
        </Box>
      )}

      {isDeductibleDataExist && (
        <Box className={classes.container}>
          <Typography className={classes.bold}>Deductible:</Typography> {deductible}
        </Box>
      )}

      {productConfiguration?.[FACT_NAMES_STRING.TERM_AND_MILEAGE] && (
        <Box className={classes.container}>
          <Typography className={classes.bold}>Term and mileage:</Typography>{' '}
          {productConfiguration[FACT_NAMES_STRING.TERM_AND_MILEAGE]}
        </Box>
      )}

      {isProductSummaryDataExist && (
        <Box className={classes.container}>{productSummary && <ReadOnlyQuill value={productSummary} />}</Box>
      )}
    </>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '6px',
    display: 'flex',
    alignItems: 'center',
  },
  bold: {
    fontWeight: 'bold',
  },
});
